import axios from "axios";
import config from "./config";
import { getAccessToken, hasSiteAccess } from "./auth";

export function todayIso() {
  return new Date().toISOString().split("T")[0];
}

function authHeaders() {
  if (!config.sendAuthHeader) return {};
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  if (!response.ok) {
    throw new Error(
      `Request failed: ${response.status} ${response.statusText}`.trim(),
    );
  }
  return response;
}

async function requestJson(url, options) {
  const response = await request(url, options);
  return response.json();
}

export async function login(username, password) {
  let response;
  try {
    response = await fetch(config.loginUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ username, password, email: username }),
    });
  } catch {
    throw new Error("Could not reach the login server.");
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.message || "Invalid credentials!");
  }
  const user = data?.user;
  if (!user) {
    throw new Error("Login succeeded but no user record was returned.");
  }
  return user;
}

// Resolves for every HTTP status (201, 207, 400, 409, 500) so the caller can
// read the per-file results the backend returns. Rejects only when the
// server could not be reached at all.
export function uploadVideos(files) {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  return axios.post(`${config.apiBaseUrl}/api/uploads`, formData, {
    headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
    validateStatus: () => true,
  });
}

export function deleteVideos(ids) {
  return request(`${config.apiBaseUrl}/api/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(ids),
  });
}

// Queues finished, failed or stopped rows again. Resolves with
// { requeued: [{id, file_name, asset_type}], skipped: [{id, reason}] }.
export function reprocessVideos(ids) {
  return requestJson(`${config.apiBaseUrl}/api/reprocess`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(ids),
  });
}

// Every row registered on a day (stopped ones included), for the day list.
export function fetchVideos(date) {
  const day = date || todayIso();
  return requestJson(
    `${config.apiBaseUrl}/api/videos?date=${encodeURIComponent(day)}`,
  );
}

// Newest notifications first: { notifications: [...], unread }.
export function fetchNotifications(limit = 100) {
  return requestJson(
    `${config.apiBaseUrl}/api/notifications?limit=${encodeURIComponent(limit)}`,
  );
}

// { all: true } or { ids: [...] }
export function markNotificationsRead(selection = { all: true }) {
  return requestJson(`${config.apiBaseUrl}/api/notifications/read`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(selection),
  });
}

export function fetchProcessCount(date) {
  const day = date || todayIso();
  return requestJson(
    `${config.apiBaseUrl}/api/getVideoProcessCount?date=${encodeURIComponent(day)}`,
  );
}

export function fetchProcessedHistory(date) {
  const base = hasSiteAccess()
    ? `${config.globalApiBaseUrl}/api/getGlobalVideoProcessedDataHistory`
    : `${config.apiBaseUrl}/api/getVideoProcessedDataHistory`;
  const url = date ? `${base}?date=${encodeURIComponent(date)}` : base;
  return requestJson(url);
}

// month: "YYYY-MM" reads one month; by: "video" (recording date, default)
// or "processed" (the videos processed in that month).
export function fetchVideoMetadata({
  refresh = false,
  month = null,
  by = null,
} = {}) {
  const params = new URLSearchParams();
  if (refresh) params.set("refresh", "1");
  if (month) params.set("month", month);
  if (month && by) params.set("by", by);
  const query = params.toString();
  return requestJson(
    `${config.metadataBaseUrl}/api/videoMetadata${query ? `?${query}` : ""}`,
  );
}

// Registers database rows for SHIFT videos the map flagged as missing.
export function registerMissingVideos(fileNames) {
  return requestJson(`${config.metadataBaseUrl}/api/orphans/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ files: fileNames }),
  });
}
